/**
 * 첫 방문 장면 모션 (시안 05 README 4.5 · MO1~MO3). 값은 `05_Handoff Screens.dc.html` 의 WalkIn · Steam · Flicker 그대로.
 *
 * - transform · opacity 만 움직입니다(MO1 의 밝기만 예외). Web Animations API
 * - 동작 줄이기(prefers-reduced-motion)면 MO1 = 600ms 크로스페이드, MO2 · MO3 = 없음
 * - 사진은 cover 로 깔리므로, 사진 안의 한 점(주전자 뚜껑 · 탁상 등)은 화면 크기에 맞춰 다시 셈합니다
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { sceneUrl } from './SceneCards.jsx';

// 장면 사진 원본 크기(시안 docs/Scene Image Prompts.md: 941×1672, 9:16)
const PHOTO = { width: 941, height: 1672 };
export const WALK_MS = 2000;
const REDUCED_FADE_MS = 600;

/** Web Animations API 가 없는 환경(시험 · 오래된 브라우저)이면 움직이지 않습니다 */
function canAnimate(element) {
    return typeof element?.animate === 'function';
}

export function prefersReducedMotion() {
    return typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}

/** 상자 크기를 따라갑니다 */
function useBoxSize(ref) {
    const [size, setSize] = useState({ width: 0, height: 0 });
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return undefined;
        }
        const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
        measure();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);
    return size;
}

/**
 * cover 로 깐 사진 안의 점(비율)을 화면 좌표로
 * @param {{ width: number, height: number }} box
 * @param {number} fx 사진 가로 비율(0~1)
 * @param {number} fy 사진 세로 비율(0~1)
 * @param {number} [posY] object-position 세로(0~1, 기본 가운데)
 */
export function coverPoint(box, fx, fy, posY = 0.5) {
    const scale = Math.max(box.width / PHOTO.width, box.height / PHOTO.height);
    const width = PHOTO.width * scale;
    const height = PHOTO.height * scale;
    return {
        x: (box.width - width) * 0.5 + fx * width,
        y: (box.height - height) * posY + fy * height,
        scale,
    };
}

/** MO2 주전자 김: 기준점 = 주전자 뚜껑(사진 가로 64% · 세로 53%). 3.2초 × 6개, 0.533초 간격, 무한 */
export function Steam() {
    const boxRef = useRef(null);
    const puffsRef = useRef(null);
    const box = useBoxSize(boxRef);
    // 김 덩어리는 상자 크기를 잰 뒤에야 그려집니다. 첫 그림(크기 0)에서 한 번만 돌면 덩어리가 없어 움직이지 않습니다
    const measured = box.width > 0;
    useEffect(() => {
        if (!measured || prefersReducedMotion() || !canAnimate(puffsRef.current?.firstElementChild)) {
            return undefined;
        }
        const animations = Array.from(puffsRef.current.children).map((puff, index) => {
            const dx = [-34, -22, -40, -16, -30, -26][index];
            const sway = [10, 14, 6, 16, 8, 12][index];
            return puff.animate(
                [
                    { transform: 'translate(0px, 0px) scale(0.5)', opacity: 0 },
                    { offset: 0.15, transform: 'translate(2px, -24px) scale(0.8)', opacity: 0.55 },
                    { offset: 0.5, transform: `translate(${sway}px, -92px) scale(1.4)`, opacity: 0.35 },
                    { transform: `translate(${dx}px, -170px) scale(2.2)`, opacity: 0 },
                ],
                {
                    duration: 3200,
                    delay: -index * 533,
                    iterations: Infinity,
                    easing: 'cubic-bezier(0.25, 0.1, 0.5, 1)',
                }
            );
        });
        return () => animations.forEach((animation) => animation.cancel());
    }, [measured]);
    const point = coverPoint(box, 0.64, 0.53);
    return (
        <span ref={boxRef} aria-hidden="true" className="pointer-events-none absolute inset-0">
            {box.width ? (
                <span ref={puffsRef} className="absolute size-0" style={{ left: point.x, top: point.y }}>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                        <span
                            key={index}
                            className="absolute rounded-full"
                            style={{
                                left: -22,
                                top: -22,
                                width: 44,
                                height: 44,
                                background: 'radial-gradient(circle, rgba(255,248,236,0.55), rgba(255,248,236,0) 68%)',
                                filter: 'blur(5px)',
                                opacity: 0,
                            }}
                        />
                    ))}
                </span>
            ) : null}
        </span>
    );
}

/** MO3 별채 호롱불: 7초 무한, 70개 지점의 매끄러운 무작위 걸음. 시안 390 폭 기준 값을 화면 폭에 맞춥니다 */
export function LampFlicker({ objectPositionY = 0.74 }) {
    const boxRef = useRef(null);
    const glowRef = useRef(null);
    const warmRef = useRef(null);
    const darkRef = useRef(null);
    const box = useBoxSize(boxRef);
    useEffect(() => {
        if (prefersReducedMotion() || !canAnimate(glowRef.current)) {
            return undefined;
        }
        let seed = 7;
        const random = () => {
            seed = (seed * 16807) % 2147483647;
            return seed / 2147483647;
        };
        const steps = 70;
        const glow = [];
        const warm = [];
        const dark = [];
        let value = 0.8;
        for (let index = 0; index <= steps; index += 1) {
            const offset = index / steps;
            value += (random() - 0.5) * 0.45;
            if (random() < 0.08) {
                value -= 0.35;
            }
            value = Math.max(0.35, Math.min(1, value));
            const end = index === steps ? 0.8 : value;
            const x = index === steps ? 0 : (random() - 0.5) * 14;
            const y = index === steps ? 0 : (random() - 0.5) * 8;
            glow.push({
                offset,
                opacity: end,
                transform: `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) scale(${(0.94 + end * 0.1).toFixed(3)})`,
            });
            warm.push({ offset, opacity: (0.35 + end * 0.65).toFixed(3) });
            dark.push({ offset, opacity: ((1 - end) * 0.22).toFixed(3) });
        }
        const options = { duration: 7000, iterations: Infinity, easing: 'ease-in-out' };
        const animations = [
            glowRef.current.animate(glow, options),
            warmRef.current.animate(warm, options),
            darkRef.current.animate(dark, options),
        ];
        return () => animations.forEach((animation) => animation.cancel());
    }, [box.width]);
    // 시안(390 폭)의 탁상 등 136, 92 → 사진 안 비율로 바꿔 지금 폭에서 다시 셉니다
    const mock = coverPoint({ width: 390, height: 540 }, 0, 0, objectPositionY);
    const lampFx = (136 - mock.x) / (PHOTO.width * mock.scale);
    const lampFy = (92 - mock.y) / (PHOTO.height * mock.scale);
    const lamp = coverPoint(box, lampFx, lampFy, objectPositionY);
    const glowSize = 240 * (box.width / 390 || 1);
    if (prefersReducedMotion()) {
        return null;
    }
    return (
        <span ref={boxRef} aria-hidden="true" className="pointer-events-none absolute inset-0">
            <span
                ref={darkRef}
                className="absolute inset-0"
                style={{
                    background: 'radial-gradient(120% 90% at 35% 17%, rgba(26,22,18,0.2), rgba(26,22,18,0.9) 75%)',
                    opacity: 0,
                }}
            />
            <span
                ref={warmRef}
                className="absolute inset-0"
                style={{
                    background:
                        'radial-gradient(90% 70% at 35% 17%, rgba(255,190,110,0.32), rgba(255,170,90,0.1) 55%, rgba(255,170,90,0) 80%)',
                    mixBlendMode: 'soft-light',
                }}
            />
            <span
                ref={glowRef}
                className="absolute rounded-full"
                style={{
                    left: lamp.x - glowSize / 2,
                    top: lamp.y - glowSize / 2,
                    width: glowSize,
                    height: glowSize,
                    background:
                        'radial-gradient(circle, rgba(255,214,150,0.55), rgba(255,190,110,0.18) 40%, rgba(255,190,110,0) 70%)',
                    mixBlendMode: 'screen',
                }}
            />
        </span>
    );
}

/**
 * MO1 걷기 전환: 골목 사진이 2초 동안 가게 문(가로 54% · 세로 48%)으로 다가가고, 1.7~2.0초에 카운터 사진(+ 김)으로 넘어갑니다.
 * scale(p) = 1 ÷ (1 − 0.5833·p), p 는 선형 시간(가까울수록 빨라짐). 키프레임마다 ease 를 주지 않습니다.
 * 동작 줄이기면 600ms 크로스페이드만. 끝나면 onDone
 */
export function WalkIn({ onDone }) {
    const bobRef = useRef(null);
    const alleyRef = useRef(null);
    const counterRef = useRef(null);
    const onDoneRef = useRef(onDone);
    onDoneRef.current = onDone;
    useEffect(() => {
        const reduced = prefersReducedMotion();
        const duration = reduced ? REDUCED_FADE_MS : WALK_MS;
        const animations = [];
        if (!canAnimate(alleyRef.current)) {
            counterRef.current.style.opacity = '1';
        } else if (!reduced) {
            const zoom = [];
            const bob = [];
            for (let index = 0; index <= 40; index += 1) {
                const p = index / 40;
                zoom.push({
                    offset: p,
                    transform: `scale(${(1 / (1 - 0.5833 * p)).toFixed(4)})`,
                    filter: `brightness(${(0.95 + 0.13 * p).toFixed(3)})`,
                });
                bob.push({ offset: p, transform: `translateY(${(-1.8 * Math.sin(Math.PI * 4 * p)).toFixed(2)}px)` });
            }
            const linear = { duration, easing: 'linear', fill: 'forwards' };
            animations.push(alleyRef.current.animate(zoom, linear), bobRef.current.animate(bob, linear));
            animations.push(
                counterRef.current.animate([{ opacity: 0 }, { offset: 1700 / WALK_MS, opacity: 0 }, { opacity: 1 }], {
                    duration,
                    fill: 'forwards',
                })
            );
        } else {
            animations.push(
                counterRef.current.animate([{ opacity: 0 }, { opacity: 1 }], { duration, fill: 'forwards' })
            );
        }
        const timer = setTimeout(() => onDoneRef.current?.(), duration);
        return () => {
            clearTimeout(timer);
            animations.forEach((animation) => animation.cancel());
        };
    }, []);
    const photo = 'absolute inset-0 size-full object-cover';
    return (
        <span className="absolute inset-0 overflow-hidden bg-text-primary" aria-hidden="true">
            <span ref={bobRef} className="absolute inset-x-0 -inset-y-1">
                <img
                    ref={alleyRef}
                    src={sceneUrl('alley')}
                    alt=""
                    className={photo}
                    style={{ transformOrigin: '54% 48%', willChange: 'transform' }}
                />
            </span>
            <span ref={counterRef} className="absolute inset-0" style={{ opacity: 0 }}>
                <img src={sceneUrl('counter')} alt="" className={photo} />
                <Steam />
            </span>
        </span>
    );
}
